import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  FaInstagram,
  FaFacebook,
  FaSyncAlt,
  FaCheckCircle,
  FaExclamationTriangle,
  FaExternalLinkAlt,
} from 'react-icons/fa';

const apiDomain = process.env.REACT_APP_API_DOMAIN;
const authDomain = process.env.REACT_APP_AUTH_DOMAIN;

/**
 * Admin page: refresh the access token that powers the @namasvinexus IG
 * business account on the feed.
 *
 * Why it exists:
 *   The Home → "Latest Feeds" rail on both the user and sponsor apps is
 *   driven by /api/v2/feed/recent, which calls Instagram Graph using the
 *   admin's stored long-lived token (RTDB path: secrets/userTokens/<uid>).
 *   Long-lived FB/IG tokens last ~60 days. When that token expires the
 *   feed silently goes dark. This page lets the admin re-login from the
 *   auth server and writes a fresh token back to RTDB.
 *
 * Which OAuth flow:
 *   The @namasvinexus IG business account is owned by a Facebook user
 *   (Trishul's FB account, providerId 122116971392403029) via Meta
 *   Business / Pages. The token currently in RTDB is a FB long-lived
 *   token at secrets/userTokens/facebook:122116971392403029 — which is
 *   why the feed code does a multi-candidate lookup (instagram:<bizId>
 *   first, falling back to the linked facebook:<userId>).
 *
 *   So the canonical refresh button uses /auth/facebook — that re-issues
 *   the same Facebook long-lived token that powers IG Graph calls for the
 *   business page. A second optional Instagram-OAuth button is provided
 *   for cases where the account is managed as a pure IG creator/business
 *   account without a backing Facebook page.
 */
const RefreshIgToken = () => {
  const [status, setStatus] = useState(null); // {present, age_days, ...}
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Diagnostic lookup — separate from the main feed-admin status so admins
  // can verify a smoke-test login (e.g. with their own account) without
  // confusing it with the feed-owner row.
  const [diagUid, setDiagUid] = useState('');
  const [diagResult, setDiagResult] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagError, setDiagError] = useState('');

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await axios.get(
        `${apiDomain}/api/v2/admin/feed-token-status`
      );
      setStatus(resp.data || {});
    } catch (e) {
      setError(
        e?.response?.data?.error ||
          e?.message ||
          'Failed to fetch token status'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDiag = async () => {
    const uid = diagUid.trim();
    if (!uid) {
      setDiagError('Enter a uid like "instagram:25495160636775674"');
      return;
    }
    setDiagLoading(true);
    setDiagError('');
    setDiagResult(null);
    try {
      const resp = await axios.get(
        `${apiDomain}/api/v2/admin/feed-token-status`,
        { params: { uid } }
      );
      setDiagResult(resp.data || {});
    } catch (e) {
      setDiagError(
        e?.response?.data?.error ||
          e?.message ||
          'Failed to read status for that uid'
      );
    } finally {
      setDiagLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // The canonical refresh path is /auth/facebook because the @namasvinexus
  // IG business account is backed by a Facebook page — the token currently
  // in RTDB is a FB long-lived token under facebook:<fb_user_id>, and
  // that's what /feed/recent picks up via its multi-candidate lookup.
  //
  // We also expose a fallback Instagram-direct URL for accounts that exist
  // as pure IG business/creator accounts (no backing FB page). reauth=true
  // is best-effort — Instagram drops it during its internal redirect chain,
  // so we still recommend incognito for the cleanest result.
  const buildAuthUrl = ({ provider }) => {
    const redirectTo = `${window.location.origin}/refresh-ig-token?just_refreshed=1`;
    const params = new URLSearchParams({
      app: 'false',
      platform: 'web',
      redirectTo,
    });
    if (provider === 'instagram') {
      params.set('iguser', 'true');
      params.set('reauth', 'true');
      return `${authDomain}/auth/instagram?${params.toString()}`;
    }
    return `${authDomain}/auth/facebook?${params.toString()}`;
  };

  const justRefreshed =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('just_refreshed') === '1';

  // After OAuth, auth server bounces back here with a ticket in the URL hash
  // for the user/sponsor apps. The admin web flow just needs the token written
  // server-side — which already happened during OAuth — so we ignore the
  // ticket here and just refetch status so the admin sees the new issued_at.
  useEffect(() => {
    if (!justRefreshed) return;
    // Small delay so namasvi-auth's RTDB write definitely lands before we read.
    const t = setTimeout(() => {
      fetchStatus();
      // Strip the ?just_refreshed=1 from the URL so the page is shareable.
      const cleanUrl =
        window.location.origin + window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
    }, 1500);
    return () => clearTimeout(t);
  }, [justRefreshed, fetchStatus]);

  const renderStatus = () => {
    if (loading) {
      return (
        <div className="flex items-center text-gray-500">
          <FaSyncAlt className="animate-spin mr-2" /> Checking current token…
        </div>
      );
    }
    if (error) {
      return (
        <div className="flex items-start text-red-600">
          <FaExclamationTriangle className="mt-1 mr-2 flex-shrink-0" />
          <div>
            <div className="font-semibold">Could not read token status</div>
            <div className="text-sm mt-1">{error}</div>
          </div>
        </div>
      );
    }
    if (!status?.present) {
      return (
        <div className="bg-amber-50 border border-amber-300 rounded p-4">
          <div className="flex items-center text-amber-800 font-semibold mb-1">
            <FaExclamationTriangle className="mr-2" /> No token stored
          </div>
          <div className="text-sm text-amber-900">
            The feed-owner account <code>{status?.uid}</code> has no stored
            access token in RTDB. The "Latest Feeds" rail will be empty for
            every user until an admin logs in below.
          </div>
        </div>
      );
    }
    const stale = !!status.needs_refresh_soon;
    return (
      <div
        className={`border rounded p-4 ${
          stale
            ? 'bg-amber-50 border-amber-300'
            : 'bg-green-50 border-green-300'
        }`}
      >
        <div
          className={`flex items-center font-semibold mb-1 ${
            stale ? 'text-amber-800' : 'text-green-800'
          }`}
        >
          {stale ? (
            <FaExclamationTriangle className="mr-2" />
          ) : (
            <FaCheckCircle className="mr-2" />
          )}
          Token present
        </div>
        <div className="text-sm text-gray-700 leading-relaxed">
          <div>
            <span className="text-gray-500">Lookup UID: </span>
            <code className="text-xs">{status.uid}</code>
          </div>
          {status.found_uid && status.found_uid !== status.uid && (
            <div>
              <span className="text-gray-500">Token stored under: </span>
              <code className="text-xs">{status.found_uid}</code>
            </div>
          )}
          <div>
            <span className="text-gray-500">Issued: </span>
            {status.issued_at_iso || '—'}{' '}
            <span className="text-gray-400">
              ({status.age_days} {status.age_days === 1 ? 'day' : 'days'} ago)
            </span>
          </div>
          {stale && (
            <div className="mt-2 text-amber-900">
              ⚠ Long-lived IG business tokens last ~60 days. Refresh now to
              avoid the feed going dark.
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 lg:p-6 max-w-3xl">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center">
          <FaInstagram className="mr-3 text-pink-500" /> Refresh Instagram
          Token
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Re-authenticate the <strong>@namasvinexus</strong> business
          account so the "Latest Feeds" rail keeps working on the user and
          sponsor apps.
        </p>
      </div>

      <div className="bg-white shadow rounded-lg p-5 space-y-5">
        <section>
          <h2 className="text-sm uppercase tracking-wide text-gray-500 mb-2">
            Current status
          </h2>
          {renderStatus()}
          <div className="mt-2">
            <button
              type="button"
              onClick={fetchStatus}
              disabled={loading}
              className="text-xs text-blue-600 hover:underline disabled:text-gray-400"
            >
              {loading ? 'Refreshing…' : 'Recheck status'}
            </button>
          </div>
        </section>

        <hr />

        <section>
          <h2 className="text-sm uppercase tracking-wide text-gray-500 mb-2">
            Refresh the token
          </h2>
          <div className="bg-blue-50 border border-blue-300 rounded p-3 text-xs text-blue-900 mb-3">
            <strong>Which button do I click?</strong>
            <ul className="list-disc list-inside mt-1 space-y-1">
              <li>
                <strong>Facebook (recommended):</strong> the @namasvinexus
                IG business account is connected to a Facebook page — the
                token currently in RTDB was originally issued via Facebook
                OAuth (under <code>facebook:122116971392403029</code>). Use
                this if Trishul's FB account owns/manages the page.
              </li>
              <li>
                <strong>Instagram (fallback):</strong> only use this if you
                want to set up the account as a pure IG creator/business
                account with no backing FB page.
              </li>
            </ul>
          </div>
          <div className="bg-red-50 border border-red-300 rounded p-3 text-xs text-red-900 mb-3">
            <strong>READ ME FIRST.</strong> Whichever provider you pick,
            Facebook and Instagram both silently reuse the account already
            signed in on this browser. To avoid binding the wrong account:
            <ol className="list-decimal list-inside mt-1 space-y-1">
              <li>
                <strong>Most reliable:</strong> open this page in an{' '}
                <strong>incognito / private window</strong>, log in to
                admin, then click one of the buttons below. Incognito has
                no FB or IG cookies → fresh login form.
              </li>
              <li>
                <strong>Or:</strong> click "Log out of Facebook" / "Log out
                of Instagram" below, close those tabs, then run the
                refresh.
              </li>
            </ol>
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <a
              href="https://www.facebook.com/logout.php"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-xs font-medium border border-gray-300"
            >
              Log out of Facebook
              <FaExternalLinkAlt className="ml-1.5 text-[10px]" />
            </a>
            <a
              href="https://www.instagram.com/accounts/logout/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-xs font-medium border border-gray-300"
            >
              Log out of Instagram
              <FaExternalLinkAlt className="ml-1.5 text-[10px]" />
            </a>
          </div>
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <a
              href={buildAuthUrl({ provider: 'facebook' })}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded shadow text-sm font-medium"
            >
              <FaFacebook className="mr-2" /> Refresh via Facebook
              (recommended)
              <FaExternalLinkAlt className="ml-2 text-xs" />
            </a>
            <a
              href={buildAuthUrl({ provider: 'instagram' })}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-pink-600 hover:bg-pink-700 text-white rounded shadow text-sm font-medium"
            >
              <FaInstagram className="mr-2" /> Refresh via Instagram
              (fallback)
              <FaExternalLinkAlt className="ml-2 text-xs" />
            </a>
          </div>
          <ol className="list-decimal list-inside text-xs text-gray-600 space-y-1">
            <li>
              Pick the right button per the blue box above. Most cases:
              Facebook.
            </li>
            <li>
              A new tab opens the official login page. Sign in with the FB
              account that manages the @namasvinexus business page (or
              with the IG account directly if you used the fallback).
            </li>
            <li>
              Approve permissions → bounces back here → status card
              refreshes within ~1.5s. Confirm{' '}
              <code>Token stored under:</code> shows{' '}
              <code>facebook:122116971392403029</code> (FB flow) OR{' '}
              <code>instagram:17841468309204143</code> (IG flow). Any other
              id = wrong account was signed in.
            </li>
          </ol>
          <div className="text-xs text-gray-500 mt-3 break-all">
            <div>
              <span className="font-semibold">FB OAuth URL:</span>{' '}
              <code>{buildAuthUrl({ provider: 'facebook' })}</code>
            </div>
            <div className="mt-1">
              <span className="font-semibold">IG OAuth URL:</span>{' '}
              <code>{buildAuthUrl({ provider: 'instagram' })}</code>
            </div>
          </div>
        </section>

        <hr />

        <section>
          <h2 className="text-sm uppercase tracking-wide text-gray-500 mb-2">
            Diagnostic — check any account's token
          </h2>
          <p className="text-xs text-gray-600 mb-2">
            The status card above only reads the feed-owner uid (
            <code>{status?.uid || '—'}</code>). If you smoke-test the OAuth
            flow with any other Instagram account, that account's token gets
            saved at its own path — use this to look it up. Format:{' '}
            <code>instagram:&lt;ig_business_id&gt;</code>.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 mb-2">
            <input
              type="text"
              value={diagUid}
              onChange={(e) => setDiagUid(e.target.value)}
              placeholder="instagram:25495160636775674"
              className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm font-mono"
            />
            <button
              type="button"
              onClick={fetchDiag}
              disabled={diagLoading || !diagUid.trim()}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-800 disabled:bg-gray-300 text-white rounded text-sm"
            >
              {diagLoading ? 'Checking…' : 'Check'}
            </button>
          </div>
          {diagError && (
            <div className="text-xs text-red-600 mt-1">{diagError}</div>
          )}
          {diagResult && (
            <div className="bg-gray-50 border border-gray-200 rounded p-3 text-xs font-mono whitespace-pre-wrap">
              {JSON.stringify(diagResult, null, 2)}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default RefreshIgToken;
